/* eslint-disable no-underscore-dangle */
/* eslint-disable no-restricted-syntax */
/* eslint-disable no-bitwise */
/**
 * Collapses an integration's predefined items (scenarios / bulks / templates)
 * into a single "collection" card in the predefined-item pickers, so one
 * partner with many items can't flood the grid.
 *
 * Rules (decided with the user):
 *  - ShopiMind items: always shown inline.
 *  - An integration with a SINGLE item (in the current filtered list): inline
 *    (a one-item collection that needs a click to reveal one card is friction).
 *  - An integration with ≥ `threshold` (default 2) items: replaced by ONE
 *    collection card. Clicking it drills in (the host sets the Auteur filter to
 *    that slug → the list re-filters to the integration's items, expanded).
 *  - Ordering: a STABLE per-session shuffle (seed captured once at load) so the
 *    order rotates across sessions for fairness but never jumps under the user.
 *  - At least one collection card is guaranteed within the first N (default 6)
 *    display slots — deterministically, not probabilistically.
 *
 * Returns the display list as `(rawItem | IntegrationCollectionMarker)[]` so the
 * host template only adds a `v-if` branch — existing `record.*` refs are
 * untouched (item entries ARE the raw records).
 */
import { computed, unref } from 'vue';
import type { Ref } from 'vue';

export interface IntegrationCollectionMarker {
  __collection: true;
  slug: string;
  name: string;
  icon?: string | null;
  count: number;
  /** shop_integration_status of the integration ('active' | 'not_installed' | …). */
  status?: string | null;
  /** Up to 3 preview image URLs gathered from the integration's items. */
  previews: string[];
}

export type IntegrationDisplayEntry<T> = T | IntegrationCollectionMarker;

interface Options {
  /** Stable id for the shuffle key (default: id / id_template / path). */
  idOf?: (r: any) => string | number;
  /** Preview image URLs of an item (default: `images_templates`). */
  previewsOf?: (r: any) => string[];
  /** Min items to collapse into a collection card. Default 2. */
  threshold?: number;
  /** Guarantee ≥1 collection card within the first N slots. Default 6. */
  guaranteeWithin?: number;
}

// Captured once per page session → stable within a session, rotates across.
const SESSION_SEED = Math.floor(Math.random() * 0xffffffff) >>> 0;

/** FNV-1a-ish hash seeded by the session seed → deterministic per (session, key). */
function stableKey(s: string): number {
  let h = (SESSION_SEED ^ 0x811c9dc5) >>> 0;
  for (let i = 0; i < s.length; i += 1) {
    h = Math.imul(h ^ s.charCodeAt(i), 0x01000193) >>> 0;
  }
  return h >>> 0;
}

const isIntegration = (r: any): boolean => r?.author === 'integration';
const slugOf = (r: any): string => r?.integration_slug ?? '';

export function useIntegrationCollections<T = any>(
  source: Ref<T[]> | (() => T[]),
  selectedAuthors: Ref<string[]>,
  opts: Options = {},
) {
  const idOf = opts.idOf ?? ((r: any) => r.id ?? r.id_template ?? r.path ?? '');
  const previewsOf =
    opts.previewsOf ??
    ((r: any) => (Array.isArray(r.images_templates) ? r.images_templates : []));
  const threshold = opts.threshold ?? 2;
  const guarantee = opts.guaranteeWithin ?? 6;

  const list = (): T[] =>
    (typeof source === 'function' ? source() : unref(source)) || [];

  const displayItems = computed<IntegrationDisplayEntry<T>[]>(() => {
    const items = list() as any[];
    if (!items.length) return [];

    // Count integration items per slug in the current (tag/search) filtered list.
    const counts = new Map<string, number>();
    for (const r of items) {
      if (isIntegration(r)) {
        const s = slugOf(r);
        if (s) counts.set(s, (counts.get(s) || 0) + 1);
      }
    }

    const entries: IntegrationDisplayEntry<any>[] = [];
    const emitted = new Set<string>();

    for (const r of items) {
      if (!isIntegration(r)) {
        entries.push(r);
        continue;
      }
      const slug = slugOf(r);
      const drilledIn = selectedAuthors.value.includes(slug);
      const count = counts.get(slug) || 0;

      if (drilledIn || count < threshold) {
        entries.push(r);
        continue;
      }
      if (emitted.has(slug)) continue;
      emitted.add(slug);

      const previews: string[] = [];
      for (const it of items) {
        if (isIntegration(it) && slugOf(it) === slug) {
          const p = previewsOf(it);
          if (p && p[0]) previews.push(p[0]);
          if (previews.length >= 3) break;
        }
      }

      entries.push({
        __collection: true,
        slug,
        name: r.integration_name ?? slug,
        icon: r.integration_icon_url ?? null,
        status: r.shop_integration_status ?? null,
        count,
        previews,
      });
    }

    // No collection card in the list → keep the source order untouched, so
    // tenants without integration items keep the exact backend ordering
    // (recency, etc.) they had before this feature.
    if (!entries.some((e) => (e as IntegrationCollectionMarker).__collection)) {
      return entries;
    }

    // Stable per-session shuffle.
    const keyed = entries.map((e) => ({
      e,
      k: stableKey(
        (e as IntegrationCollectionMarker).__collection
          ? `c:${(e as IntegrationCollectionMarker).slug}`
          : `i:${String(idOf(e))}`,
      ),
    }));
    keyed.sort((a, b) => a.k - b.k);
    const ordered = keyed.map((x) => x.e);

    // Guarantee a collection card within the first `guarantee` slots.
    const firstColl = ordered.findIndex(
      (e) => (e as IntegrationCollectionMarker).__collection,
    );
    if (firstColl >= guarantee) {
      const [coll] = ordered.splice(firstColl, 1);
      ordered.splice(Math.min(2, ordered.length), 0, coll);
    }
    return ordered;
  });

  const hasCollections = computed(() =>
    displayItems.value.some(
      (e) => (e as IntegrationCollectionMarker).__collection,
    ),
  );

  /**
   * The single integration currently drilled into (else null). True only when
   * EXACTLY one author is selected and it's a real integration — a mixed
   * selection (ShopiMind + integration, or two integrations) is not a drill-in,
   * so the per-card ribbons stay visible to keep provenance clear.
   */
  const drilledInIntegration = computed(() => {
    if (selectedAuthors.value.length !== 1) return null;
    const slug = selectedAuthors.value[0];
    const match = (list() as any[]).find((r) => isIntegration(r) && slugOf(r) === slug);
    if (!match) return null; // 'shopimind' / 'me' / unknown → not a drill-in
    return {
      slug,
      name: match.integration_name ?? slug,
      icon: match.integration_icon_url ?? null,
    };
  });

  return { displayItems, hasCollections, drilledInIntegration };
}
